import { getTime, getUnixTime } from 'date-fns';
import { err, ok } from 'neverthrow';
import {
  delay,
  firstValueFrom,
  identity,
  of,
  bufferCount,
  from,
  mergeMap,
  toArray,
  concatMap,
  lastValueFrom,
  defer,
  timeout,
  retry,
} from 'rxjs';
import type { Observable, ObservableInput } from 'rxjs';
import type { UnixTimestamp } from './types';

export function parseEnv(env: '1' | '0' | 'true' | 'false' | string | undefined): string | boolean {
  if (!env) {
    return false;
  }
  try {
    return Boolean(JSON.parse(env));
  } catch {
    return env;
  }
}

/**
 * JSON.parse() の例外処理を安全にし、結果を Result 型で返却する
 */
export function safeJSONParse(text: string) {
  try {
    const parsed = JSON.parse(text) as unknown;
    return ok(parsed);
  } catch (e) {
    if (e instanceof Error) {
      return err(e);
    }
    return err(new Error(`Unknown error during JSON.parse: ${String(e)}`));
  }
}

export function isString(maybeString: unknown): maybeString is string {
  return typeof maybeString === 'string';
}

/**
 * 現在時刻の unix timestamp の秒 (10 桁) number を返す。システム全体的に 10 桁 (秒) を使用しているため、 13 桁 (ミリ秒) の Date.now() を使わずこちらを使用すること
 */
export function getCurrentTimestamp(): UnixTimestamp {
  return getUnixTime(new Date());
}

/**
 * 現在時刻の unix timestamp のミリ秒 (13 桁) number を返す。Date.now() を使用せずこちらを使用すること
 * @warning システム全体的に 10 桁 (秒) を使用しているため、ミリ秒のタイムスタンプは trace 関連でのみ使用すること
 */
export function getCurrentMsTimestamp(): UnixTimestamp {
  return getTime(new Date());
}

/**
 * List に対してページング、並列処理を制御した処理を行う関数
 * パラメータが何も指定されない場合、dataList を全て 1 度の fn 呼び出しで処理する
 *
 * @example 1 要素ずつ、 2 並列で、 100ms 間隔で実行する
 * const data = await limitConcurrent(dataList, { buffer: 1, concurrency: 2, delay: 100 }, asyncFn);
 * @example 5 要素ずつ、 2 並列で、 100ms 間隔で実行する
 * const data = await limitConcurrent(dataList, { buffer: 5, concurrency: 2, delay: 100 }, asyncFn);

 * 1 件ずつ直列実行したいのみの場合は mapSequentialAsync を使用する
 * @see mapSequentialAsync
 *
 * デバッグ/テスト用途で $ に Observable が格納されるが、型シグネチャ上は隠蔽され、通常は関数呼び出しを使用して Promise として扱う
 * @see Testing.ExposeObservable
 */
export function limitConcurrency<T, U>(
  dataList: T[],
  {
    /**
     * fn に渡すデータをいくつずつまとめるかの値
     * @note 0 以下の数値が指定された場合は 1 として扱う
     * @default dataList.length
     */
    buffer: _buffer = dataList.length,
    /**
     * 最大並列数
     * buffer の数で dataList を先に分割するため、 buffer 値によっては指定の並列数にならない
     * @default 1
     */
    concurrency: _concurrency = 1,
    /**
     * @default 0
     */
    delay: delayMs = 0,
  }: { buffer?: number; concurrency?: number; delay?: number },
  /** 返り値は一般に Promise<U> を想定 */
  fn: (data: T[], index: number) => ObservableInput<U>
): Promise<U[]> {
  const buffer = _buffer <= 0 ? 1 : _buffer; // なるべく小さい数になるように計算しているつもりで誤りで 0 を下回ってしまったときのために、安全寄りでデフォルト値ではなく 1 にする
  const concurrency = _concurrency <= 0 ? 1 : _concurrency;
  const $ = from(dataList).pipe(
    bufferCount(buffer),
    mergeMap(
      (buffered, index) =>
        of(buffered).pipe(
          mergeMap(fn),
          // concurrency 以下の index の場合は初回処理なので delay を入れない
          index < concurrency ? identity : delay(delayMs)
        ),
      concurrency
    )
  );

  const lastValuePromise = firstValueFrom(
    $.pipe(
      mergeMap((v) => (Array.isArray(v) ? v : [v])),
      toArray()
    )
  );

  // for debugging and testing expose
  (lastValuePromise as typeof lastValuePromise & { $: Observable<U | U[]> }).$ = $;

  // 返り値の型情報上は $ は隠蔽されるため、テスト、デバッグ用途で必要な場合は Testing.exposeObservable を使用してアクセスする
  return lastValuePromise;
}

/**
 * dataList の要素を fn に渡し、 1 件ずつ async 関数を直列に順序保証で実行する
 *
 * options で、関数 1 回あたりのタイムアウト、リトライ回数、要素間/リトライ間の delay (ms) を指定できる。
 * delay は各要素の間隔とリトライ試行間隔の両方に使用される
 */
export async function mapSequentialAsync<T, U>(
  dataList: T[],
  {
    /**
     * fn 1 回の実行に対するタイムアウト (ms) 。未指定の場合はタイムアウトなし
     */
    timeoutMs,
    /**
     * @default 0
     */
    retryCount = 0,
    /**
     * リトライを含む、関数の各実行の間の delay ミリ秒
     * @default 0
     */
    delay: delayMs = 0,
  }: { timeoutMs?: number; retryCount?: number; delay?: number },
  /** 返り値は一般に Promise<U> を想定 */
  fn: (data: T, index: number) => ObservableInput<U>
): Promise<U[]> {
  return await lastValueFrom(
    from(dataList).pipe(
      concatMap((data, index) =>
        defer(() => from(fn(data, index))).pipe(
          timeoutMs !== undefined ? timeout({ each: timeoutMs }) : identity,
          retry({ count: retryCount, delay: delayMs }),
          delayMs && index < dataList.length - 1 ? delay(delayMs) : identity
        )
      ),
      toArray()
    )
  );
}
